import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import loader from "../../Images/ajax-loader.gif";
import {
  getDrugAddictionInspection,
  saveDrugAddictionInspection,
  updateDrugAddictionInspection
} from "../../api/User";

const translations = {
  en: {
    back: "Back",
    title: "Status of Drug Addiction",
    langToggle: "हिन्दी",
    q1: "1. Have Any Drug Abuse Cases Been Reported in the Village?",
    yes: "Yes",
    no: "No",
    q2: "2. If Yes, Approximate Number of Cases",
    selectRange: "Select Range",
    q3: "3. Awareness Programme Conducted By",
    gramPanchayat: "Gram Panchayat",
    school: "School",
    police: "Police",
    healthDepartment: "Health Department",
    ngo: "NGO",
    others: "Others",
    othersSpecify: "Please specify other awareness providers",
    q4: "4. Youth Participation in Awareness Programmes",
    good: "Good",
    average: "Average",
    poor: "Poor",
    q5: "5. Is There a Nasha Mukti Kendra (De-addiction Centre) Available Nearby?",
    q6: "6. Have Any Persons Been Referred to a Nasha Mukti Kendra for Treatment?",
    referredCountLabel: "If Yes, Number of Persons Referred",
    q7: "7. Overall Status of Drug Abuse in the Village",
    underControl: "Under Control",
    needsAttention: "Needs Attention",
    seriousConcern: "Serious Concern",
    cancel: "Cancel",
    save: "Save",
    update: "Update",
    saving: "Saving...",
    validationError: "Please resolve the validation errors in the form.",
    rangeError: "Please select approximate range of cases",
    referredCountError: "Please enter a valid positive number for referred count",
    othersError: "Please specify other awareness providers",
    confirmTitle: "Confirm Save",
    confirmMsgSave: "After saving you will allow only edit once. Do you want to proceed?",
    confirmMsgUpdate: "You will not be allowed to edit this form after saving. Do you want to proceed?",
    okay: "Okay",
  },
  hi: {
    back: "पीछे",
    title: "गाँव में नशे की स्थिति",
    langToggle: "English",
    q1: "1. क्या गाँव में नशीले पदार्थों के सेवन के मामले सामने आए हैं?",
    yes: "हाँ",
    no: "नहीं",
    q2: "2. यदि हाँ, तो मामलों की अनुमानित संख्या",
    selectRange: "श्रेणी चुनें",
    q3: "3. जागरूकता कार्यक्रम किसके द्वारा आयोजित किया गया",
    gramPanchayat: "ग्राम पंचायत",
    school: "स्कूल",
    police: "पुलिस",
    healthDepartment: "स्वास्थ्य विभाग",
    ngo: "गैर सरकारी संगठन / एनजीओ",
    others: "अन्य",
    othersSpecify: "कृपया अन्य जागरूकता प्रदाताओं का उल्लेख करें",
    q4: "4. जागरूकता कार्यक्रमों में युवाओं की भागीदारी",
    good: "अच्छी",
    average: "औसत",
    poor: "कमजोर/खराब",
    q5: "5. क्या पास में कोई नशा मुक्ति केंद्र उपलब्ध है?",
    q6: "6. क्या किसी व्यक्ति को उपचार के लिए नशा मुक्ति केंद्र भेजा गया है?",
    referredCountLabel: "यदि हाँ, तो भेजे गए व्यक्तियों की संख्या",
    q7: "7. गाँव में नशीले पदार्थों के सेवन की समग्र स्थिति",
    underControl: "नियंत्रण में",
    needsAttention: "ध्यान देने की आवश्यकता है",
    seriousConcern: "गंभीर चिंता का विषय",
    cancel: "रद्द करें",
    save: "सहेजें",
    update: "अपडेट करें",
    saving: "सहेज रहा है...",
    validationError: "कृपया फॉर्म में त्रुटियों को ठीक करें।",
    rangeError: "कृपया मामलों की अनुमानित सीमा चुनें",
    referredCountError: "कृपया भेजे गए व्यक्तियों की संख्या दर्ज करें",
    othersError: "कृपया अन्य जागरूकता प्रदाताओं का उल्लेख करें",
    confirmTitle: "बदलाव सहेजें",
    confirmMsgSave: "सहेजने के बाद आपको केवल एक बार एडिट करने की अनुमति होगी। क्या आप आगे बढ़ना चाहते हैं?",
    confirmMsgUpdate: "इस बार सहेजने के बाद आपको इस फॉर्म को दोबारा एडिट करने की अनुमति नहीं होगी। क्या आप आगे बढ़ना चाहते हैं?",
    okay: "ठीक है",
  }
};

const DrugAddictionForm = () => {
  const { meetingId } = useParams();
  const [searchParams] = useSearchParams();
  const gpId = searchParams.get("gp");
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");
  const userId = sessionStorage.getItem("user_id");

  const [lang, setLang] = useState("en"); // English default
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recordId, setRecordId] = useState(null);
  const [isFrozen, setIsFrozen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  // Form Fields
  const [drugCasesReported, setDrugCasesReported] = useState(false);
  const [approxCaseRange, setApproxCaseRange] = useState("");
  const [awarenessBy, setAwarenessBy] = useState({
    gramPanchayat: false,
    school: false,
    police: false,
    healthDepartment: false,
    ngo: false,
    others: false,
  });
  const [otherAwarenessDetails, setOtherAwarenessDetails] = useState("");
  const [youthParticipation, setYouthParticipation] = useState("Average");
  const [nearbyNashaMuktiCenter, setNearbyNashaMuktiCenter] = useState(true);
  const [personsReferred, setPersonsReferred] = useState(false);
  const [referredCount, setReferredCount] = useState("");
  const [overallStatus, setOverallStatus] = useState("Under Control");

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const loadRecord = async () => {
      try {
        setLoading(true);
        const res = await getDrugAddictionInspection(token, meetingId);
        if (res.status === "success" && res.data) {
          const data = res.data;
          setRecordId(data.id);
          setDrugCasesReported(!!data.drug_cases_reported);
          setApproxCaseRange(data.approx_case_range || "");
          
          // Parse awareness conducted by JSON
          const awareness = data.awareness_by || [];
          setAwarenessBy({
            gramPanchayat: awareness.includes("Gram Panchayat"),
            school: awareness.includes("School"),
            police: awareness.includes("Police"),
            healthDepartment: awareness.includes("Health Department"),
            ngo: awareness.includes("NGO"),
            others: awareness.some(a => a.startsWith("Others:")),
          });
          const otherStr = awareness.find(a => a.startsWith("Others:"));
          if (otherStr) {
            setOtherAwarenessDetails(otherStr.replace("Others: ", ""));
          }

          setYouthParticipation(data.youth_participation || "Average");
          setNearbyNashaMuktiCenter(!!data.nearby_nasha_mukti_center);
          setPersonsReferred(!!data.persons_referred);
          setReferredCount(data.referred_count !== null ? data.referred_count : "");
          setOverallStatus(data.overall_status || "Under Control");

          // Handle freeze logic
          if (data.updated_by !== null && data.updated_by !== 0) {
            setIsFrozen(true);
          }
        }
      } catch (error) {
        console.error("Error loading drug addiction inspection details:", error);
        toast.error("Failed to load existing inspection details.", { autoClose: 2000 });
      } finally {
        setLoading(false);
      }
    };

    if (meetingId) {
      loadRecord();
    }
  }, [meetingId, token]);

  useEffect(() => {
    const role = sessionStorage.getItem("role_id");
    if (["1", "3", "7", "8"].includes(role)) {
      setIsFrozen(true);
    }
  }, []);

  const handleAwarenessChange = (event) => {
    if (isFrozen) return;
    setAwarenessBy({
      ...awarenessBy,
      [event.target.name]: event.target.checked,
    });
  };

  const isFormValid = () => {
    if (isFrozen) return false;

    // Q1 check
    if (drugCasesReported && !approxCaseRange) return false;
    
    // Q3: Must have at least one checked
    const hasAwareness = awarenessBy.gramPanchayat || awarenessBy.school || awarenessBy.police || awarenessBy.healthDepartment || awarenessBy.ngo || awarenessBy.others;
    if (!hasAwareness) return false;
    if (awarenessBy.others && !otherAwarenessDetails.trim()) return false;
    
    // Q6 check
    if (personsReferred && (referredCount === "" || isNaN(referredCount) || parseInt(referredCount) < 0)) return false;

    return true;
  };

  const validate = () => {
    const errors = {};
    if (drugCasesReported && !approxCaseRange) {
      errors.approxCaseRange = translations[lang].rangeError;
    }
    if (personsReferred && (referredCount === "" || isNaN(referredCount) || parseInt(referredCount) < 0)) {
      errors.referredCount = translations[lang].referredCountError;
    }
    if (awarenessBy.others && !otherAwarenessDetails.trim()) {
      errors.otherAwarenessDetails = translations[lang].othersError;
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = (e) => {
    e.preventDefault();
    if (isFrozen) return;
    if (!validate()) {
      toast.error(translations[lang].validationError, { autoClose: 2000 });
      return;
    }
    setOpenConfirm(true);
  };

  const executeSave = async () => {
    setOpenConfirm(false);
    try {
      setSaving(true);
      // Compile awareness by list
      const awarenessList = [];
      if (awarenessBy.gramPanchayat) awarenessList.push("Gram Panchayat");
      if (awarenessBy.school) awarenessList.push("School");
      if (awarenessBy.police) awarenessList.push("Police");
      if (awarenessBy.healthDepartment) awarenessList.push("Health Department");
      if (awarenessBy.ngo) awarenessList.push("NGO");
      if (awarenessBy.others && otherAwarenessDetails.trim()) {
        awarenessList.push(`Others: ${otherAwarenessDetails.trim()}`);
      }

      const payload = {
        meeting_id: parseInt(meetingId),
        gram_panchayat_id: parseInt(gpId || sessionStorage.getItem("gp_data") || 0),
        drug_cases_reported: drugCasesReported,
        approx_case_range: drugCasesReported ? approxCaseRange : "",
        awareness_by: awarenessList,
        youth_participation: youthParticipation,
        nearby_nasha_mukti_center: nearbyNashaMuktiCenter,
        persons_referred: personsReferred,
        referred_count: personsReferred ? parseInt(referredCount) : 0,
        overall_status: overallStatus,
        remarks: "", // Remarks field removed
      };

      let res;
      if (recordId) {
        payload.updated_by = parseInt(userId);
        res = await updateDrugAddictionInspection(token, recordId, payload);
      } else {
        payload.created_by = parseInt(userId);
        res = await saveDrugAddictionInspection(token, payload);
      }

      if (res.status === "success") {
        toast.success(res.message || "Saved successfully!", { autoClose: 1500 });
        setTimeout(() => {
          navigate("/gram-sabha-inspection");
        }, 1500);
      } else {
        toast.error(res.message || "Failed to save form record.");
      }
    } catch (error) {
      console.error("Error saving form:", error);
      toast.error("An error occurred while saving the form.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <img src={loader} alt="loading..." />
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", width: "100%" }}>
      <ToastContainer />

      {/* Confirmation Modal */}
      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title" style={{ fontWeight: "bold" }}>
          {translations[lang].confirmTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description" style={{ color: "#333" }}>
            {recordId ? translations[lang].confirmMsgUpdate : translations[lang].confirmMsgSave}
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "15px 24px" }}>
          <Button 
            onClick={() => setOpenConfirm(false)} 
            variant="outlined" 
            style={{ textTransform: "none", color: "#666", borderColor: "#ccc" }}
          >
            {translations[lang].cancel}
          </Button>
          <Button 
            onClick={executeSave} 
            variant="contained" 
            autoFocus
            style={{ textTransform: "none", backgroundColor: "#4763E4", color: "#fff" }}
          >
            {translations[lang].okay}
          </Button>
        </DialogActions>
      </Dialog>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <Button 
            onClick={() => navigate("/gram-sabha-inspection")} 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            style={{ textTransform: "none", color: "#4763E4", borderColor: "#4763E4" }}
          >
            {translations[lang].back}
          </Button>
          <h3 style={{ margin: 0, fontWeight: "bold" }}>
            {translations[lang].title}
          </h3>
        </div>
        <Button 
          onClick={() => setLang(lang === "en" ? "hi" : "en")} 
          variant="outlined" 
          style={{ textTransform: "none", color: "#4763E4", borderColor: "#4763E4", fontWeight: "bold" }}
        >
          {translations[lang].langToggle}
        </Button>
      </div>
      <hr style={{ border: "1px solid #ddd", marginBottom: "30px" }} />

      
      {!isFrozen && recordId && (
        <div style={{
          backgroundColor: "#FEF3C7",
          color: "#92400E",
          border: "1px solid #FCD34D",
          padding: "15px",
          borderRadius: "6px",
          marginBottom: "25px",
          fontWeight: "bold",
          fontSize: "14px"
        }}>
          ⚠️ {lang === "en" ? "Note: You can edit this form only one time." : "ध्यान दें: आप इस फॉर्म को केवल एक ही बार एडिट कर सकते हैं।"}
        </div>
      )}

      <form onSubmit={handleSaveClick} style={{ background: "#fff", padding: "30px", borderRadius: "8px", boxShadow: "0 4px 10px rgba(0, 0, 0, 0.05)", width: "100%" }}>
        
        {/* 1. Have Any Drug Abuse Cases Been Reported in the Village? */}
        <div style={{ marginBottom: "25px" }}>
          <h5 style={{ fontWeight: "bold", marginBottom: "5px" }}>{translations[lang].q1}</h5>
          <FormControl component="fieldset">
            <RadioGroup row value={drugCasesReported ? "Yes" : "No"} onChange={(e) => setDrugCasesReported(e.target.value === "Yes")}>
              <FormControlLabel value="Yes" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].yes} />
              <FormControlLabel value="No" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].no} />
            </RadioGroup>
          </FormControl>
        </div>

        {/* 2. Approximate Number of Cases */}
        {drugCasesReported && (
          <div style={{ marginBottom: "25px" }}>
            <h5 style={{ fontWeight: "bold", marginBottom: "10px" }}>{translations[lang].q2}</h5>
            <FormControl size="small" style={{ width: "200px" }}>
              <InputLabel id="approx-range-label">{translations[lang].selectRange}</InputLabel>
              <Select
                labelId="approx-range-label"
                value={approxCaseRange}
                label={translations[lang].selectRange}
                onChange={(e) => setApproxCaseRange(e.target.value)}
                disabled={isFrozen}
                error={!!formErrors.approxCaseRange}
              >
                <MenuItem value="0-10">0-10</MenuItem>
                <MenuItem value="10-20">10-20</MenuItem>
                <MenuItem value="20-30">20-30</MenuItem>
                <MenuItem value="30-40">30-40</MenuItem>
                <MenuItem value="40-50">40-50</MenuItem>
                <MenuItem value="50-60">50-60</MenuItem>
                <MenuItem value="60-70">60-70</MenuItem>
                <MenuItem value="70-80">70-80</MenuItem>
                <MenuItem value="80-90">80-90</MenuItem>
                <MenuItem value="90-100">90-100</MenuItem>
              </Select>
            </FormControl>
          </div>
        )}

        {/* 3. Awareness Programme Conducted By */}
        <div style={{ marginBottom: "25px" }}>
          <h5 style={{ fontWeight: "bold", marginBottom: "10px" }}>{translations[lang].q3}</h5>
          <FormGroup row>
            <FormControlLabel
              control={<Checkbox checked={awarenessBy.gramPanchayat} onChange={handleAwarenessChange} name="gramPanchayat" color="primary" disabled={isFrozen} />}
              label={translations[lang].gramPanchayat}
            />
            <FormControlLabel
              control={<Checkbox checked={awarenessBy.school} onChange={handleAwarenessChange} name="school" color="primary" disabled={isFrozen} />}
              label={translations[lang].school}
            />
            <FormControlLabel
              control={<Checkbox checked={awarenessBy.police} onChange={handleAwarenessChange} name="police" color="primary" disabled={isFrozen} />}
              label={translations[lang].police}
            />
            <FormControlLabel
              control={<Checkbox checked={awarenessBy.healthDepartment} onChange={handleAwarenessChange} name="healthDepartment" color="primary" disabled={isFrozen} />}
              label={translations[lang].healthDepartment}
            />
            <FormControlLabel
              control={<Checkbox checked={awarenessBy.ngo} onChange={handleAwarenessChange} name="ngo" color="primary" disabled={isFrozen} />}
              label={translations[lang].ngo}
            />
            <FormControlLabel
              control={<Checkbox checked={awarenessBy.others} onChange={handleAwarenessChange} name="others" color="primary" disabled={isFrozen} />}
              label={translations[lang].others}
            />
          </FormGroup>
          {awarenessBy.others && (
            <TextField
              label={translations[lang].othersSpecify}
              fullWidth
              size="small"
              variant="outlined"
              style={{ marginTop: "10px" }}
              value={otherAwarenessDetails}
              onChange={(e) => setOtherAwarenessDetails(e.target.value)}
              disabled={isFrozen}
              error={!!formErrors.otherAwarenessDetails}
              helperText={formErrors.otherAwarenessDetails}
            />
          )}
        </div>

        {/* 4. Youth Participation in Awareness Programmes */}
        <div style={{ marginBottom: "25px" }}>
          <h5 style={{ fontWeight: "bold", marginBottom: "5px" }}>{translations[lang].q4}</h5>
          <FormControl component="fieldset">
            <RadioGroup row value={youthParticipation} onChange={(e) => setYouthParticipation(e.target.value)}>
              <FormControlLabel value="Good" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].good} />
              <FormControlLabel value="Average" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].average} />
              <FormControlLabel value="Poor" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].poor} />
            </RadioGroup>
          </FormControl>
        </div>

        {/* 5. Is There a Nasha Mukti Kendra (De-addiction Centre) Available Nearby? */}
        <div style={{ marginBottom: "25px" }}>
          <h5 style={{ fontWeight: "bold", marginBottom: "5px" }}>{translations[lang].q5}</h5>
          <FormControl component="fieldset">
            <RadioGroup row value={nearbyNashaMuktiCenter ? "Yes" : "No"} onChange={(e) => setNearbyNashaMuktiCenter(e.target.value === "Yes")}>
              <FormControlLabel value="Yes" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].yes} />
              <FormControlLabel value="No" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].no} />
            </RadioGroup>
          </FormControl>
        </div>

        {/* 6. Have Any Persons Been Referred to a Nasha Mukti Kendra for Treatment? */}
        <div style={{ marginBottom: "25px" }}>
          <h5 style={{ fontWeight: "bold", marginBottom: "5px" }}>{translations[lang].q6}</h5>
          <FormControl component="fieldset">
            <RadioGroup row value={personsReferred ? "Yes" : "No"} onChange={(e) => setPersonsReferred(e.target.value === "Yes")}>
              <FormControlLabel value="Yes" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].yes} />
              <FormControlLabel value="No" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].no} />
            </RadioGroup>
          </FormControl>
          {personsReferred && (
            <TextField
              label={translations[lang].referredCountLabel}
              type="number"
              size="small"
              variant="outlined"
              style={{ marginTop: "10px", width: "450px" }}
              value={referredCount}
              onChange={(e) => setReferredCount(e.target.value)}
              disabled={isFrozen}
              error={!!formErrors.referredCount}
              helperText={formErrors.referredCount}
              inputProps={{ min: "0" }}
            />
          )}
        </div>

        {/* 7. Overall Status of Drug Abuse in the Village */}
        <div style={{ marginBottom: "25px" }}>
          <h5 style={{ fontWeight: "bold", marginBottom: "5px" }}>{translations[lang].q7}</h5>
          <FormControl component="fieldset">
            <RadioGroup row value={overallStatus} onChange={(e) => setOverallStatus(e.target.value)}>
              <FormControlLabel value="Under Control" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].underControl} />
              <FormControlLabel value="Needs Attention" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].needsAttention} />
              <FormControlLabel value="Serious Concern" control={<Radio color="primary" disabled={isFrozen} />} label={translations[lang].seriousConcern} />
            </RadioGroup>
          </FormControl>
        </div>

        {/* Submit */}
        <div style={{ display: "flex", gap: "15px", justifyContent: "flex-end", alignItems: "center" }}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => navigate("/gram-sabha-inspection")}
            disabled={saving}
            style={{ textTransform: "none", color: "#666", borderColor: "#ccc" }}
          >
            {translations[lang].cancel}
          </Button>
          {!isFrozen && (
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !isFormValid()}
              style={{ textTransform: "none", backgroundColor: isFormValid() ? "#4763E4" : "#ccc" }}
            >
              {saving ? translations[lang].saving : recordId ? translations[lang].update : translations[lang].save}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default DrugAddictionForm;
